import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  PresentationChartBarIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { FaPlus, FaTrash } from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useConfirm } from "../contexts/ConfirmContext";
import QuizPop from "./QuizPop";
import RibbonTiptapEditor from "./RibbonTiptapEditor";

// Helper function to strip HTML tags
const stripHtmlTags = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const contentTypeOptions = [
  { value: "class", label: "Class (Video)" },
  { value: "note", label: "Notes" },
  { value: "ppt", label: "PPT" },
  { value: "question", label: "Quiz" },
];

// Utility function to check if a file is a PDF
const isPdfFile = (file) => file && file.type === "application/pdf";

// Ensure API base always ends with /api so /contents and /lessons resolve to /api/contents, /api/lessons
const rawApiUrl = process.env.REACT_APP_API_URL || "https://api.isyllabi.com/api";
const API_URL = rawApiUrl.endsWith("/api") ? rawApiUrl : rawApiUrl.replace(/\/?$/, "") + "/api";

/** GET /lessons/:id returns snake_case; parent NewCourse uses camelCase. Normalize so visibility + forms stay consistent after refetch. */
function normalizeLessonPayload(data) {
  if (!data || typeof data !== "object") return data;
  const contents = Array.isArray(data.contents) ? data.contents : [];
  return {
    ...data,
    videoUrl: data.videoUrl ?? data.video_url ?? "",
    url: data.url ?? "",
    contents,
  };
}

/** Initial section visibility from server + content presence. Only call when opening a lesson — not after every content refetch. */
function computeVisibilityFromLesson(lwc) {
  if (!lwc || typeof lwc !== "object") {
    return { class: false, note: false, ppt: false, question: false };
  }
  const c = lwc.contents || [];
  const videoUrl = lwc.videoUrl ?? lwc.video_url ?? "";
  const hasClass =
    !!videoUrl ||
    !!lwc.url ||
    !!(lwc.description && String(lwc.description).trim() !== "") ||
    c.some((item) => item.type === "class");
  const hasNote = c.some((item) => item.type === "note");
  const hasPpt = c.some((item) => item.type === "ppt");
  const hasQuestion = c.some((item) => item.type === "question");
  const read = (v) => {
    if (v === null || v === undefined) return false;
    return !!Number(v);
  };
  return {
    class: hasClass && read(lwc.show_class),
    note: hasNote && read(lwc.show_note),
    ppt: hasPpt && read(lwc.show_ppt),
    question: hasQuestion && read(lwc.show_question),
  };
}

/** Class tab form fields from lesson row (normalized snake/camel). */
function classFormStateFromLessonPayload(lwc) {
  if (!lwc || typeof lwc !== "object") {
    return {
      title: "",
      description: "",
      type: "video",
      url: "",
      content: "",
      duration: 0,
      file: null,
      videoUrl: "",
    };
  }
  return {
    title: lwc.title || "",
    description: lwc.description || "",
    type: "video",
    url: lwc.url || "",
    content: "",
    duration: lwc.duration ?? 0,
    file: null,
    videoUrl: lwc.videoUrl ?? lwc.video_url ?? "",
  };
}

// Test function to check if the API is working properly
const testApiEndpoints = async () => {
  try {
    console.log("Testing API endpoints...");

    // Test simple JSON endpoint
    const simpleResponse = await axios.get(
      "http://localhost:8000/api/test/simple-json"
    );
    console.log("Simple JSON response:", simpleResponse.data);

    // Test bypass middleware endpoint
    const bypassResponse = await axios.get(
      "http://localhost:8000/api/test/bypass-middleware"
    );
    console.log("Bypass middleware response:", bypassResponse.data);

    // Test no middleware endpoint
    const noMiddlewareResponse = await axios.get(
      "http://localhost:8000/api/test/no-middleware"
    );
    console.log("No middleware response:", noMiddlewareResponse.data);

    // Test raw endpoint
    const rawResponse = await axios.get("http://localhost:8000/api/test/raw");
    console.log("Raw response:", rawResponse.data);

    // Test content mimic endpoint
    const mimicResponse = await axios.put(
      `http://localhost:8000/api/test/content-mimic/1`,
      {
        title: "Test Title",
        type: "note",
        description: "Test Description",
      }
    );
    console.log("Content mimic response:", mimicResponse.data);

    // Test raw content update endpoint
    const rawContentResponse = await axios.put(
      `http://localhost:8000/api/test/content-raw/1`,
      {
        title: "Raw Test Title",
        type: "note",
        description: "Raw Test Description",
      }
    );
    console.log("Raw content response:", rawContentResponse.data);

    // Test content structure endpoint
    const structureResponse = await axios.put(
      `http://localhost:8000/api/test/content-structure/1`,
      {
        title: "Structure Test Title",
        type: "note",
        description: "Structure Test Description",
      }
    );
    console.log("Content structure response:", structureResponse.data);

    // Test ultra raw endpoint
    const ultraRawResponse = await axios.put(
      `http://localhost:8000/api/test/ultra-raw/1`,
      {
        title: "Ultra Raw Test Title",
        type: "note",
        description: "Ultra Raw Test Description",
      }
    );
    console.log("Ultra raw response:", ultraRawResponse.data);

    // Test debug content endpoint
    const debugResponse = await axios.put(
      `http://localhost:8000/api/test/debug-content/1`,
      {
        title: "Debug Test Title",
        type: "note",
        description: "Debug Test Description",
        content: "<p>Test content</p>",
      }
    );
    console.log("Debug content response:", debugResponse.data);

    // Test ultra raw Laravel endpoint
    const ultraRawLaravelResponse = await axios.put(
      `http://localhost:8000/api/test/ultra-raw-laravel/1`,
      {
        title: "Ultra Raw Laravel Test Title",
        type: "note",
        description: "Ultra Raw Laravel Test Description",
      }
    );
    console.log("Ultra raw Laravel response:", ultraRawLaravelResponse.data);

    // Test simple PHP file (bypasses Laravel entirely)
    const simplePhpResponse = await axios.post(
      "http://localhost:8000/test-simple.php",
      {
        title: "Simple PHP Test Title",
        type: "note",
        description: "Simple PHP Test Description",
      }
    );
    console.log("Simple PHP response:", simplePhpResponse.data);
  } catch (error) {
    console.error("API test error:", error);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
      console.error("Response headers:", error.response.headers);

      // Try to decode if it's an encoded response
      if (
        typeof error.response.data === "string" &&
        error.response.data.length > 100
      ) {
        const decoded = tryDecodeResponse(error.response.data);
        if (decoded) {
          console.log("Decoded response:", decoded);
        }
      }
    }
  }
};

// Function to try to decode encoded response
const tryDecodeResponse = (encodedData) => {
  try {
    // Try base64 decode
    const decoded = atob(encodedData);
    console.log("Base64 decoded:", decoded);
    return decoded;
  } catch (e) {
    console.log("Not base64 encoded");
  }

  try {
    // Try URL decode
    const decoded = decodeURIComponent(encodedData);
    console.log("URL decoded:", decoded);
    return decoded;
  } catch (e) {
    console.log("Not URL encoded");
  }

  return null;
};

const CourseContentManagerModal = ({ courseId, lesson, onContentUpdate, onClose, courseName }) => {
  const { confirm } = useConfirm();
  const [activeTab, setActiveTab] = useState("class");
  const [tabMinimized, setTabMinimized] = useState(false);
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localVideoUrl, setLocalVideoUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // State to store lesson with contents
  const [lessonWithContents, setLessonWithContents] = useState(lesson);
  
  // State for frontend visibility checkboxes (default unchecked for new class)
  const [visibilitySettings, setVisibilitySettings] = useState({
    class: false,
    note: false,
    ppt: false,
    question: false,
  });

  // Class form (lesson fields) — must be declared before effects that hydrate it from GET /lessons/:id
  const [classFormData, setClassFormData] = useState({
    title: lesson && lesson.title ? lesson.title : "",
    description: lesson && lesson.description ? lesson.description : "",
    type: "video",
    url: lesson && lesson.url ? lesson.url : "",
    content: "",
    duration: lesson && lesson.duration ? lesson.duration : 0,
    file: null,
    videoUrl: lesson?.videoUrl || "",
  });

  // Hold URL of the PPT we just saved so preview shows immediately (before refetch)
  const lastSavedPptUrlRef = useRef(null);

  // Sync lessonWithContents when parent passes updated lesson (e.g. after content save/refetch)
  useEffect(() => {
    if (lesson && lesson.contents && Array.isArray(lesson.contents)) {
      setLessonWithContents(normalizeLessonPayload(lesson));
    }
  }, [lesson]);

  // Fetch lesson when id changes; hydrate section checkboxes once per lesson open (not on later refetches — avoids quiz save clearing other sections).
  useEffect(() => {
    if (lesson?.id) {
      let cancelled = false;
      const fetchLessonContents = async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `${API_URL}/lessons/${lesson.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          if (cancelled) return;
          const normalized = normalizeLessonPayload(response.data);
          setLessonWithContents(normalized);
          setVisibilitySettings(computeVisibilityFromLesson(normalized));
          setClassFormData(classFormStateFromLessonPayload(normalized));
        } catch (error) {
          console.error("Error fetching lesson contents:", error);
          if (cancelled) return;
          const fallback = normalizeLessonPayload(lesson);
          setLessonWithContents(fallback);
          setVisibilitySettings(computeVisibilityFromLesson(fallback));
          setClassFormData(classFormStateFromLessonPayload(fallback));
        }
      };
      fetchLessonContents();
      return () => {
        cancelled = true;
      };
    }
    const n = normalizeLessonPayload(lesson);
    setLessonWithContents(n);
    setVisibilitySettings(computeVisibilityFromLesson(n));
    setClassFormData(classFormStateFromLessonPayload(n));
    return undefined;
  }, [lesson?.id]);

  // Refetch lesson contents (e.g. after saving note/ppt/question so data shows when reopening)
  const refetchLessonContents = useCallback(async () => {
    if (!lesson?.id) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/lessons/${lesson.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLessonWithContents(normalizeLessonPayload(response.data));
    } catch (err) {
      console.error("Error refetching lesson contents:", err);
    }
  }, [lesson?.id]);

  // Find content for this class (if any) - use lessonWithContents instead of lesson
  const contents = lessonWithContents?.contents || [];
  const note = contents.find((c) => c.type === "note");
  const ppts = contents.filter((c) => c.type === "ppt"); // Get ALL PPTs, not just first one
  const ppt = ppts[0]; // Keep first PPT for backward compatibility with form
  const questions = contents.filter((c) => c.type === "question");
  const question = questions[0]; // Keep first question for backward compatibility

  // Visibility is NOT synced from lessonWithContents on every refetch — that was clearing Class/Notes/PPT after saving a quiz.

  // Separate state for each content type
  const [noteFormData, setNoteFormData] = useState({
    title: "",
    type: "note",
    url: "",
    content: "",
    duration: 0,
    file: null,
  });

  const [pptFormData, setPptFormData] = useState({
    title: "",
    description: "",
    type: "ppt",
    url: "",
    content: "",
    duration: 0,
    file: null,
    displayMode: "inline", // Added for new ppt form
    autoAdvance: 0, // Added for new ppt form
  });

  // Blob URL for pre-save PDF preview (selected file). Only revoke when replacing with another file, never the current one.
  const [pptPreviewBlobUrl, setPptPreviewBlobUrl] = useState(null);
  const pptPreviewBlobUrlRef = useRef(null);
  const MAX_PDF_PREVIEW_SIZE = 15 * 1024 * 1024; // 15MB
  useEffect(() => {
    if (pptFormData.file && isPdfFile(pptFormData.file) && pptFormData.file.size <= MAX_PDF_PREVIEW_SIZE) {
      if (pptPreviewBlobUrlRef.current) {
        URL.revokeObjectURL(pptPreviewBlobUrlRef.current);
        pptPreviewBlobUrlRef.current = null;
      }
      const url = URL.createObjectURL(pptFormData.file);
      pptPreviewBlobUrlRef.current = url;
      setPptPreviewBlobUrl(url);
      return () => {
        if (pptPreviewBlobUrlRef.current) {
          URL.revokeObjectURL(pptPreviewBlobUrlRef.current);
          pptPreviewBlobUrlRef.current = null;
        }
      };
    }
    if (pptPreviewBlobUrlRef.current) {
      URL.revokeObjectURL(pptPreviewBlobUrlRef.current);
      pptPreviewBlobUrlRef.current = null;
    }
    setPptPreviewBlobUrl(null);
  }, [pptFormData.file]);

  const [questionFormData, setQuestionFormData] = useState({
    title: "",
    description: "",
    type: "question",
    url: "",
    content: "",
    duration: 0,
    file: null,
    options: [
      { text: "", desc: "" },
      { text: "", desc: "" },
      { text: "", desc: "" },
      { text: "", desc: "" },
    ],
    correctAnswer: 0,
    // Add a unique key to force remount if needed
    _new: Date.now(),
  });

  // Class form is filled from GET /lessons/:id in the lesson?.id effect (full server row). Do not also sync on every lessonWithContents bump — that used to lock after the first partial parent snapshot so Save All sent empty class data.

  // Sync note form from server only when lesson or note identity changes. Do NOT run when switching tabs or unsaved input would disappear.
  const lastNoteFormSyncRef = useRef({ lessonId: null, noteId: null });
  useEffect(() => {
    const lessonId = lesson?.id ?? null;
    const noteId = note?.id ?? null;
    if (lastNoteFormSyncRef.current.lessonId === lessonId && lastNoteFormSyncRef.current.noteId === noteId) return;
    lastNoteFormSyncRef.current = { lessonId, noteId };
    if (note) {
      setNoteFormData({
        title: note.title || "",
        type: "note",
        url: note.url || "",
        content: note.content || "",
        duration: note.duration || 0,
        file: null,
      });
    } else {
      setNoteFormData({
        title: "",
        type: "note",
        url: "",
        content: "",
        duration: 0,
        file: null,
      });
    }
  }, [lesson?.id, note]);

  // Sync ppt form from server only when lesson or ppt identity changes. Do NOT run when switching tabs or unsaved input would disappear.
  const lastPptFormSyncRef = useRef({ lessonId: null, pptId: null });
  useEffect(() => {
    const lessonId = lesson?.id ?? null;
    const pptId = ppt?.id ?? null;
    if (lastPptFormSyncRef.current.lessonId === lessonId && lastPptFormSyncRef.current.pptId === pptId) return;
    lastPptFormSyncRef.current = { lessonId, pptId };
    if (ppt) {
      setPptFormData({
        title: ppt.title || "",
        description: ppt.description || "",
        type: "ppt",
        url: ppt.url || "",
        content: ppt.content || "",
        duration: ppt.duration || 0,
        file: null,
        displayMode: ppt.displayMode || ppt.display_mode || "inline",
        autoAdvance: ppt.autoAdvance || ppt.auto_advance || 0,
      });
    } else {
      lastSavedPptUrlRef.current = null;
      setPptFormData({
        title: "",
        description: "",
        type: "ppt",
        url: "",
        content: "",
        duration: 0,
        file: null,
        displayMode: "inline",
        autoAdvance: 0,
      });
    }
  }, [lesson?.id, ppt]);

  // Quiz popup: add or edit question in a modal (not inline)
  const [showQuizPop, setShowQuizPop] = useState(false);
  const [quizPopEditContent, setQuizPopEditContent] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);

  // Add state to track video updates for re-rendering
  const [videoUpdateKey, setVideoUpdateKey] = useState(0);

  // Cleanup local video URL on unmount
  useEffect(() => {
    return () => {
      if (localVideoUrl) {
        URL.revokeObjectURL(localVideoUrl);
      }
    };
  }, [localVideoUrl]);

  // Chunked upload function for large files
  const uploadFileInChunks = async (file, lessonId, token) => {
    // Optimize chunk size based on file size
    let chunkSize = 1024 * 1024; // 1MB default
    const fileSizeMB = file.size / 1024 / 1024;

    if (fileSizeMB > 1000) {
      // For files > 1GB
      chunkSize = 5 * 1024 * 1024; // 5MB chunks
    } else if (fileSizeMB > 500) {
      // For files > 500MB
      chunkSize = 2 * 1024 * 1024; // 2MB chunks
    }

    const totalChunks = Math.ceil(file.size / chunkSize);
    let uploadedChunks = 0;

    console.log(
      `Starting optimized chunked upload: ${totalChunks} chunks of ${
        chunkSize / 1024 / 1024
      }MB each`
    );

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("chunk", chunk);
      formData.append("chunkIndex", chunkIndex);
      formData.append("totalChunks", totalChunks);
      formData.append("fileName", file.name);
      formData.append("fileSize", file.size);
      formData.append("lessonId", lessonId);

      try {
        const response = await axios.post(`${API_URL}/upload-chunk`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000, // 60 seconds per chunk for larger chunks
        });

        uploadedChunks++;
        const progress = Math.round((uploadedChunks / totalChunks) * 100);
        setUploadProgress(progress);

        console.log(
          `Chunk ${chunkIndex + 1}/${totalChunks} uploaded successfully`
        );

        // Show progress more frequently for large files
        if (fileSizeMB > 500 && progress % 5 === 0) {
          toast.info(`Upload progress: ${progress}%`);
        } else if (progress % 10 === 0) {
          toast.info(`Upload progress: ${progress}%`);
        }
      } catch (error) {
        console.error(`Error uploading chunk ${chunkIndex + 1}:`, error);
        throw new Error(
          `Failed to upload chunk ${chunkIndex + 1}: ${error.message}`
        );
      }
    }

    console.log("All chunks uploaded successfully");
    return true;
  };

  // Handle input changes for different forms
  const handleClassInputChange = async (e) => {
    const { name, value, files } = e.target;
    if (name === "file" && files && files[0]) {
      if (classFormData.url && classFormData.url.length > 0) {
        const ok = await confirm({
          title: "Replace YouTube link?",
          message: "A YouTube link is already set. Uploading a new video will remove the link. Continue?",
          confirmLabel: "Continue",
          cancelLabel: "Cancel",
          variant: "warning",
        });
        if (!ok) {
          e.target.value = "";
          return;
        }
        toast.info("YouTube link removed. Uploaded video will be used.");
        setClassFormData((prev) => ({ ...prev, file: files[0], url: "" }));
      } else {
        setClassFormData((prev) => ({ ...prev, file: files[0] }));
      }
      const file = files[0];
      if (file && file.type.startsWith("video/")) {
        const videoUrl = URL.createObjectURL(file);
        setLocalVideoUrl(videoUrl);
        setVideoUpdateKey((prev) => prev + 1);
        toast.success("Video preview updated!");
      }
    } else if (name === "url") {
      if (classFormData.file && value) {
        const ok = await confirm({
          title: "Replace uploaded video?",
          message: "A video file is already uploaded. Entering a YouTube link will remove the uploaded video. Continue?",
          confirmLabel: "Continue",
          cancelLabel: "Cancel",
          variant: "warning",
        });
        if (!ok) return;
        toast.info("Uploaded video removed. YouTube link will be used.");
        setClassFormData((prev) => ({ ...prev, url: value, file: null }));
        if (localVideoUrl) {
          URL.revokeObjectURL(localVideoUrl);
          setLocalVideoUrl(null);
        }
      } else {
        setClassFormData((prev) => ({ ...prev, url: value }));
      }
    } else {
      setClassFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleNoteInputChange = (e) => {
    const { name, value, files } = e.target;
    setNoteFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handlePptInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "file" && files && files[0]) {
      const file = files[0];

      // Check file size (50MB limit)
      const maxSize = 50 * 1024 * 1024; // 50MB in bytes
      if (file.size > maxSize) {
        toast.error(
          `File size must be less than 50MB. Current size: ${(
            file.size /
            1024 /
            1024
          ).toFixed(2)}MB`
        );
        return;
      }

      // Only allow PDF for inline viewing
      if (
        pptFormData.displayMode === "inline" &&
        file.type !== "application/pdf" &&
        !file.name.toLowerCase().endsWith(".pdf")
      ) {
        toast.error(
          "Only PDF files can be displayed inline. Please select a PDF or change the display mode."
        );
        return;
      }
      // Allow ppt/pptx for download mode
      if (
        pptFormData.displayMode !== "inline" &&
        ![
          "application/pdf",
          "application/vnd.ms-powerpoint",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ].includes(file.type) &&
        !file.name.toLowerCase().endsWith(".pdf") &&
        !file.name.toLowerCase().endsWith(".ppt") &&
        !file.name.toLowerCase().endsWith(".pptx")
      ) {
        toast.error("Only PDF or PowerPoint files are allowed.");
        return;
      }

      setPptFormData((prev) => ({ ...prev, file }));
    } else {
      setPptFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleQuestionInputChange = (e) => {
    const { name, value, files } = e.target;
    setQuestionFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // Add handler for option changes
  const handleOptionChange = (idx, value, field = "text") => {
    setQuestionFormData((prev) => {
      const newOptions = prev.options.map((opt, i) =>
        i === idx ? { ...opt, [field]: value } : opt
      );
      return { ...prev, options: newOptions };
    });
  };

  // Persist section visibility (checkboxes) to lesson so iSyllabi content page shows only active sections
  const saveVisibilityToLesson = useCallback(async () => {
    if (!lesson?.id) return;
    try {
      await axios.patch(
        `${API_URL}/lessons/${lesson.id}/visibility`,
        {
          show_class: visibilitySettings.class ? 1 : 0,
          show_note: visibilitySettings.note ? 1 : 0,
          show_ppt: visibilitySettings.ppt ? 1 : 0,
          show_question: visibilitySettings.question ? 1 : 0,
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
    } catch (err) {
      // Non-blocking; visibility will persist when user saves Class
    }
  }, [lesson?.id, visibilitySettings]);

  // Save or update class. Content is always saved (checked or unchecked); show_class only controls frontend visibility.
  // options.skipClose: when true (e.g. from Save All), do not call onContentUpdate/onClose or show toast.
  const handleSaveClass = async (e, options = {}) => {
    e.preventDefault();

    const lessonOrderRaw =
      lessonWithContents?.order !== undefined && lessonWithContents?.order !== null
        ? lessonWithContents.order
        : lesson?.order;
    const lessonOrder =
      lessonOrderRaw !== undefined && lessonOrderRaw !== null && lessonOrderRaw !== ""
        ? Number(lessonOrderRaw)
        : 0;

    // Check if description contains large content (like base64 images)
    const MAX_DESCRIPTION_SIZE = 200000; // Reduced to 200KB for better compatibility
    const isLargeContent =
      classFormData.description &&
      classFormData.description.length > MAX_DESCRIPTION_SIZE;

    // Show warning for large content
    if (
      classFormData.description &&
      classFormData.description.length > 100000
    ) {
      const sizeInMB = (classFormData.description.length / 1024 / 1024).toFixed(
        1
      );
      console.warn(
        `Large content detected: ${sizeInMB}MB. Consider compressing images.`
      );
    }

    // Only show uploading/progress if a file is being uploaded or content is large
    if (classFormData.file || isLargeContent) {
      setUploading(true);
      setUploadProgress(0);

      try {
        const token = localStorage.getItem("token");
        const formData = new FormData();

        formData.append("title", classFormData.title);
        formData.append("description", classFormData.description);
        formData.append("url", classFormData.url);
        formData.append(
          "duration",
          classFormData.duration && Number(classFormData.duration) >= 1
            ? Number(classFormData.duration)
            : 1
        );
        formData.append("order", Number.isFinite(lessonOrder) ? lessonOrder : 0);
        formData.append("show_class", visibilitySettings.class ? 1 : 0);
        formData.append("show_note", visibilitySettings.note ? 1 : 0);
        formData.append("show_ppt", visibilitySettings.ppt ? 1 : 0);
        formData.append("show_question", visibilitySettings.question ? 1 : 0);

        if (classFormData.file) {
          formData.append("file", classFormData.file);
        }

        const headers = {
          Authorization: `Bearer ${token}`,
        };

        await axios.put(`${API_URL}/lessons/${lesson.id}`, formData, {
          headers,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        });

        setUploading(false);
        setUploadProgress(0);
        if (!options.skipClose) {
          toast.success("Class updated successfully");
          if (onContentUpdate) onContentUpdate();
          if (onClose) onClose();
        }
        return;
      } catch (error) {
        setUploading(false);
        setUploadProgress(0);
        console.error("Upload error:", error);
        if (error.response?.status === 413) {
          toast.error(
            "File or content is too large. Please compress images or reduce content size."
          );
        } else {
          toast.error("Failed to update class");
        }
        throw error;
      }
    }

    // If no file and content is small, just send JSON update
    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      const dataToSend = {
        title: classFormData.title,
        description: classFormData.description,
        url: classFormData.url,
        order: Number.isFinite(lessonOrder) ? lessonOrder : 0,
        duration:
          classFormData.duration && Number(classFormData.duration) >= 1
            ? Number(classFormData.duration)
            : 1,
        show_class: visibilitySettings.class ? 1 : 0,
        show_note: visibilitySettings.note ? 1 : 0,
        show_ppt: visibilitySettings.ppt ? 1 : 0,
        show_question: visibilitySettings.question ? 1 : 0,
      };
      await axios.put(`${API_URL}/lessons/${lesson.id}`, dataToSend, {
        headers,
      });
      if (!options.skipClose) {
        toast.success("Class updated successfully");
        if (onContentUpdate) onContentUpdate();
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("Update error:", error);
      if (error.response?.status === 413) {
        toast.error(
          "Content is too large. Please compress images or reduce content size."
        );
      } else {
        toast.error("Failed to update class");
      }
      throw error;
    }
  };

  // Save or update note. Content is always saved (checked or unchecked); show_note only controls frontend visibility.
  // options.skipClose: when true (e.g. from Save All), do not call onContentUpdate/onClose or refetch.
  const handleSaveNote = async (e, options = {}) => {
    e.preventDefault();

    if (!options.skipClose) showInstantSuccess("Saving note...");

    // OPTIMISTIC UPDATE: Apply changes immediately
    const originalNote = note ? { ...note } : null;
    if (note && note.id) {
      Object.assign(note, {
        title: noteFormData.title,
        content: noteFormData.content,
        url: noteFormData.url,
        duration: noteFormData.duration,
      });
    }

    try {
      const token = localStorage.getItem("token");
      let dataToSend;
      let headers = { Authorization: `Bearer ${token}` };

      // Allow up to 1GB (1 * 1024 * 1024 * 1024 bytes)
      const MAX_NOTE_CONTENT_SIZE = 1 * 1024 * 1024 * 1024; // 1GB
      if (
        noteFormData.content &&
        noteFormData.content.length > MAX_NOTE_CONTENT_SIZE
      ) {
        showInstantError(
          `Content is extremely large (${(
            noteFormData.content.length /
            1024 /
            1024
          ).toFixed(
            1
          )}MB). This may cause browser or server issues. Please consider uploading images as files instead of embedding base64 images in the note.`
        );
        // Optionally, you can still allow the save to proceed, but warn the user
        // return; // Remove this line to allow save to proceed
      }
      // Always use FormData for large content
      if (
        noteFormData.file ||
        (noteFormData.content && noteFormData.content.length > 500000)
      ) {
        dataToSend = new FormData();
        dataToSend.append("lesson_id", lesson.id);
        dataToSend.append("title", noteFormData.title);
        dataToSend.append("type", "note");
        dataToSend.append("url", noteFormData.url);
        dataToSend.append(
          "duration",
          noteFormData.duration && Number(noteFormData.duration) >= 1
            ? Number(noteFormData.duration)
            : 1
        );
        if (noteFormData.file) dataToSend.append("file", noteFormData.file);
        if (noteFormData.content)
          dataToSend.append("content", noteFormData.content);
      } else {
        dataToSend = {
          ...noteFormData,
          lesson_id: lesson.id,
          duration:
            noteFormData.duration && Number(noteFormData.duration) >= 1
              ? Number(noteFormData.duration)
              : 1,
        };
      }

      let response;
      if (note) {
        // Update existing note
        console.log("Updating existing note with ID:", note.id);
        console.log("Request URL:", `${API_URL}/contents/${note.id}`);

        try {
          response = await axios.put(
            `${API_URL}/contents/${note.id}`,
            dataToSend,
            {
              headers,
            }
          );

          console.log("Update response status:", response.status);
          console.log("Update response data:", response.data);

          if (!options.skipClose) showInstantSuccess("Note updated successfully");
        } catch (error) {
          // Check if response is encoded
          if (
            error.response?.data &&
            typeof error.response.data === "string" &&
            error.response.data.length > 100
          ) {
            console.log("Detected encoded response");

            // Try to decode the response
            const decoded = tryDecodeResponse(error.response.data);
            if (decoded) {
              console.log("Decoded response:", decoded);
            }
          }

          // Check for SQL truncation error
          if (
            error.response?.data?.error &&
            error.response.data.error.includes("SQLSTATE[22001]")
          ) {
            showInstantError(
              "Content too large. Please reduce the size of your note or remove some images.",
              () => {
                // Rollback optimistic update
                if (originalNote) {
                  Object.assign(note, originalNote);
                }
              }
            );
            throw error;
          }

          // Show actual backend error message if available
          const msg =
            error.response?.data?.error ||
            error.message ||
            "Failed to save note";
          showInstantError(`Failed to save note: ${msg}`, () => {
            // Rollback optimistic update
            if (originalNote) {
              Object.assign(note, originalNote);
            }
          });
          throw error;
        }
      } else {
        // Add new note
        console.log("Creating new note");
        console.log("Request URL:", `${API_URL}/contents`);
        console.log("Request headers:", headers);
        console.log("Data being sent:", dataToSend);
        if (dataToSend instanceof FormData) {
          console.log("FormData contents:");
          for (let [key, value] of dataToSend.entries()) {
            console.log(key, ":", value);
          }
        }

        response = await axios.post(`${API_URL}/contents`, dataToSend, {
          headers,
        });

        console.log("Create response status:", response.status);
        console.log("Create response headers:", response.headers);
        console.log("Create response data:", response.data);

        if (!options.skipClose) showInstantSuccess("Note created successfully");
      }

      // Clear form data after successful save
      setNoteFormData({
        title: "",
        type: "note",
        url: "",
        content: "",
        duration: 0,
        file: null,
      });

      setShowAddNoteModal(false);
      if (!options.skipClose) {
        await saveVisibilityToLesson();
        await refetchLessonContents();
        if (onContentUpdate) onContentUpdate();
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("Note save error details:", error);

      // Rollback optimistic update on error
      if (originalNote) {
        Object.assign(note, originalNote);
      }

      if (error.response?.data?.error) {
        showInstantError("Failed to save note: " + error.response.data.error);
      } else if (error.response?.data?.message) {
        showInstantError("Failed to save note: " + error.response.data.message);
      } else {
        showInstantError("Failed to save note: " + error.message);
      }
      throw error;
    }
  };

  // Save or update ppt. Content is always saved (checked or unchecked); show_ppt only controls frontend visibility.
  // options.skipClose: when true (e.g. from Save All), do not call onContentUpdate/onClose or refetch.
  const handleSavePpt = async (e, options = {}) => {
    e.preventDefault();
    if (!lesson) return;
    if (pptFormData.file) {
      if (
        pptFormData.displayMode === "inline" &&
        !isPdfFile(pptFormData.file)
      ) {
        toast.error(
          "Only PDF files can be displayed inline. Please select a PDF or change the display mode."
        );
        return;
      }
      if (!isPdfFile(pptFormData.file)) {
        toast.info(
          "Non-PDF files will only be available for download, not inline viewing."
        );
      }
    }
    try {
      let dataToSend;
      let headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      if (pptFormData.file) {
        dataToSend = new FormData();
        dataToSend.append("lesson_id", lesson.id);
        dataToSend.append("title", pptFormData.title);
        dataToSend.append("type", "ppt");
        dataToSend.append("url", pptFormData.url);
        dataToSend.append("description", pptFormData.description || "");
        dataToSend.append("display_mode", pptFormData.displayMode || "inline");
        dataToSend.append("auto_advance", pptFormData.autoAdvance || 0);
        // PATCH: Always send duration >= 1
        dataToSend.append(
          "duration",
          pptFormData.duration && Number(pptFormData.duration) >= 1
            ? Number(pptFormData.duration)
            : 1
        );
        dataToSend.append("file", pptFormData.file);
      } else {
        // PATCH: Always send duration >= 1 and lesson_id for POST
        dataToSend = {
          ...pptFormData,
          lesson_id: lesson.id,
          duration:
            pptFormData.duration && Number(pptFormData.duration) >= 1
              ? Number(pptFormData.duration)
              : 1,
        };
      }
      let response;
      if (ppt) {
        // Update existing ppt
        response = await axios.put(`${API_URL}/contents/${ppt.id}`, dataToSend, {
          headers,
          timeout: 300000, // 5 minutes timeout for large uploads
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        });
        if (!options.skipClose) toast.success("PPT updated successfully");
      } else {
        // Add new ppt
        response = await axios.post(`${API_URL}/contents`, dataToSend, {
          headers,
          timeout: 300000, // 5 minutes timeout for large uploads
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        });
        if (!options.skipClose) toast.success("PPT added successfully");
      }
      // Update form with saved URL so PPT/PDF Preview shows immediately (per Display Mode)
      const saved = response?.data;
      const savedUrl = saved?.url ?? saved?.fileUrl ?? null;
      if (savedUrl) {
        lastSavedPptUrlRef.current = savedUrl;
        setPptFormData((prev) => ({
          ...prev,
          url: savedUrl,
          displayMode: saved?.display_mode ?? saved?.displayMode ?? prev.displayMode,
          autoAdvance: saved?.auto_advance ?? saved?.autoAdvance ?? prev.autoAdvance,
          file: null, // clear file so preview uses server URL
        }));
      }
      if (!options.skipClose) {
        await saveVisibilityToLesson();
        await refetchLessonContents();
        if (onContentUpdate) onContentUpdate();
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("PPT save error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      console.error("Request URL:", error.config?.url);
      console.error("Request method:", error.config?.method);

      let errorMessage = "Failed to save PPT";

      if (error.code === "ERR_NETWORK") {
        errorMessage = "Network error - unable to connect to server";
      } else if (error.code === "ERR_HTTP2_PROTOCOL_ERROR") {
        errorMessage = "HTTP/2 protocol error - server connection issue";
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(`Failed to save PPT: ${errorMessage}`);
      throw error;
    }
  };

  // Save or update question. Content is always saved (checked or unchecked); show_question only controls frontend visibility.
  // options.skipClose: when true (e.g. from Save All), do not call onContentUpdate/onClose or refetch.
  const handleSaveQuestion = async (e, options = {}) => {
    e.preventDefault();
    if (!lesson) return;
    try {
      let dataToSend;
      let headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      // Store options and correctAnswer in content as JSON
      const contentJson = JSON.stringify({
        question: questionFormData.title,
        options: questionFormData.options.map((opt) =>
          opt.text !== undefined ? opt.text : opt
        ),
        correctAnswer: questionFormData.correctAnswer,
        correctOptionDesc: questionFormData.correctOptionDesc,
        description: questionFormData.description,
      });
      dataToSend = {
        lesson_id: lesson.id,
        title: questionFormData.title,
        description: questionFormData.description,
        type: "question",
        url: questionFormData.url,
        content: contentJson,
        duration: questionFormData.duration,
      };
      headers["Content-Type"] = "application/json";
      if (editingQuestionId) {
        await axios.put(
          `${API_URL}/contents/${editingQuestionId}`,
          dataToSend,
          { headers }
        );
        if (!options.skipClose) toast.success("Question updated successfully");
      } else {
        await axios.post(`${API_URL}/contents`, dataToSend, { headers });
        if (!options.skipClose) toast.success("Question added successfully");
      }
      setShowQuestionForm(false);
      setEditingQuestionId(null);
      if (!options.skipClose) {
        await saveVisibilityToLesson();
        await refetchLessonContents();
        if (onContentUpdate) onContentUpdate();
        if (onClose) onClose();
      }
    } catch (error) {
      toast.error("Failed to save question");
      throw error;
    }
  };

  // Save all sections (Class, Note, PPT, Quiz) in one go. Not section-dependent; saves all currently filled data.
  const handleSaveAll = async () => {
    const fakeEvent = { preventDefault: () => {} };
    if (!lesson?.id) {
      toast.error("Save the lesson first.");
      return;
    }
    setInstantFeedback(true);
    try {
      await handleSaveClass(fakeEvent, { skipClose: true });
      if (note || (noteFormData.title || noteFormData.content)) {
        await handleSaveNote(fakeEvent, { skipClose: true });
      }
      if (ppt || (pptFormData.title || pptFormData.file)) {
        await handleSavePpt(fakeEvent, { skipClose: true });
      }
      // Quiz is saved via QuizPop only; skip here
      await saveVisibilityToLesson();
      await refetchLessonContents();
      if (onContentUpdate) onContentUpdate();
      if (onClose) onClose();
      toast.success("All sections saved successfully.");
    } catch (err) {
      console.error("Save all error:", err);
      toast.error("Some sections could not be saved. See messages above.");
    } finally {
      setInstantFeedback(false);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    // INSTANT FEEDBACK: Show immediate success
    showInstantSuccess("Deleting lesson...");

    // OPTIMISTIC UPDATE: Remove from UI immediately
    const originalLesson = lesson;
    if (lesson && lesson.id === lessonId) {
      // Mark as deleted in UI
      lesson.deleted = true;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showInstantSuccess("Lesson deleted successfully");
      onContentUpdate();
    } catch (error) {
      console.error("Error deleting lesson:", error);

      // Rollback optimistic update on error
      if (originalLesson) {
        Object.assign(lesson, originalLesson);
      }

      showInstantError("Failed to delete lesson: " + error.message);
    }
  };

  const handleDeleteContent = async (lessonId, contentId) => {
    const ok = await confirm({
      title: "Delete content",
      message: "Are you sure you want to delete this content?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;

    // INSTANT FEEDBACK: Show immediate success
    showInstantSuccess("Deleting content...");

    // OPTIMISTIC UPDATE: Remove from UI immediately
    const originalContent = lessonWithContents?.contents?.find((c) => c.id === contentId);
    if (originalContent) {
      originalContent.deleted = true;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      console.log("Deleting content:", contentId, "from lesson:", lessonId);
      const response = await axios.delete(`${API_URL}/contents/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Delete response:", response.data);
      showInstantSuccess("Content deleted successfully");
      
      // Refresh lesson contents
      if (lesson?.id) {
        const fetchLessonContents = async () => {
          try {
            const token = localStorage.getItem("token");
            const response = await axios.get(
              `${API_URL}/lessons/${lesson.id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            setLessonWithContents(normalizeLessonPayload(response.data));
          } catch (error) {
            console.error("Error refreshing lesson contents:", error);
          }
        };
        fetchLessonContents();
      }
      
      onContentUpdate();
    } catch (error) {
      console.error("Error deleting content:", error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Rollback optimistic update on error
      if (originalContent) {
        Object.assign(originalContent, { deleted: false });
      }

      const errorMessage = error.response?.data?.message || error.message || "Failed to delete content";
      showInstantError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // Add optimistic update states
  const [optimisticUpdates, setOptimisticUpdates] = useState({});
  const [instantFeedback, setInstantFeedback] = useState(false);

  // Optimistic update helper
  const applyOptimisticUpdate = (action, data) => {
    setOptimisticUpdates((prev) => ({
      ...prev,
      [action]: { data, timestamp: Date.now() },
    }));

    // Apply instant UI feedback
    setInstantFeedback(true);
    setTimeout(() => setInstantFeedback(false), 2000); // Reset after 2 seconds
  };

  // Instant success feedback
  const showInstantSuccess = (message) => {
    toast.success(message, { duration: 1500 });
    setInstantFeedback(true);
    setTimeout(() => setInstantFeedback(false), 1500);
  };

  // Instant error feedback with rollback
  const showInstantError = (message, rollbackAction) => {
    toast.error(message, { duration: 3000 });
    if (rollbackAction) {
      rollbackAction();
    }
  };

  const handleTabChange = (tab) => {
    // INSTANT FEEDBACK: Immediate tab switch
    setActiveTab(tab);
    showInstantSuccess(`Switched to ${tab} tab`);
  };

  const handleCloseModal = () => {
    // INSTANT FEEDBACK: Immediate modal close
    showInstantSuccess("Closing modal...");
    setShowAddLessonModal(false);
    setShowAddNoteModal(false);
    setShowAddPptModal(false);
    setShowAddQuestionModal(false);
  };

  const handleOpenModal = (modalType) => {
    // INSTANT FEEDBACK: Immediate modal open
    showInstantSuccess(`Opening ${modalType} form...`);
    if (modalType === "class") setShowAddLessonModal(true);
    if (modalType === "note") setShowAddNoteModal(true);
    if (modalType === "ppt") setShowAddPptModal(true);
    if (modalType === "question") setShowAddQuestionModal(true);
  };

  // Add missing modal state for add note, ppt, and question
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [showAddPptModal, setShowAddPptModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);

  if (!lesson) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-white rounded-lg shadow-lg overflow-hidden" style={{ height: 'calc(90vh - 2rem)' }}>
      <style>{`
  .quill-preview {
    font-size: 0.95rem;
    line-height: 1.7;
    word-break: break-word;
  }
  .quill-preview ul, .quill-preview ol {
    padding-left: 1.5em;
    margin-bottom: 1em;
  }
  .quill-preview li {
    margin-bottom: 0.25em;
  }
  .quill-preview .ql-align-center { text-align: center; }
  .quill-preview .ql-align-right { text-align: right; }
  .quill-preview .ql-align-justify { text-align: justify; }
  .quill-preview img { max-width: 100%; height: auto; border-radius: 8px; }
`}</style>
      {/* Header: one row — left: Course Content + sections; right: Course Content Manager + course name */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50/80">
        <div className="px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {/* Left: Course Content label + section tabs */}
          <div className="flex flex-col gap-2 min-w-0">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Course Content</h2>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500 mr-1" title="Unchecked = hidden on course page. Check to show this section to students.">
                Show on course page:
              </span>
              <button
                type="button"
                onClick={() => setActiveTab("class")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  activeTab === "class"
                    ? "bg-cyan-600 text-white shadow-md"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-cyan-300 hover:bg-cyan-50/50"
                }`}
              >
                <VideoCameraIcon className="h-5 w-5" />
                <span>Classes</span>
                <input
                  type="checkbox"
                  checked={visibilitySettings.class}
                  onChange={(e) => { e.stopPropagation(); setVisibilitySettings(prev => ({ ...prev, class: e.target.checked })); }}
                  className="w-4 h-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  title="Show Classes section on course page (uncheck to hide)"
                  onClick={(e) => e.stopPropagation()}
                />
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("note")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  activeTab === "note"
                    ? "bg-amber-500 text-white shadow-md"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-amber-300 hover:bg-amber-50/50"
                }`}
              >
                <DocumentTextIcon className="h-5 w-5" />
                <span>Notes</span>
                <input
                  type="checkbox"
                  checked={visibilitySettings.note}
                  onChange={(e) => { e.stopPropagation(); setVisibilitySettings(prev => ({ ...prev, note: e.target.checked })); }}
                  className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                  title="Show Notes section on course page (uncheck to hide)"
                  onClick={(e) => e.stopPropagation()}
                />
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("ppt")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  activeTab === "ppt"
                    ? "bg-purple-600 text-white shadow-md"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-purple-300 hover:bg-purple-50/50"
                }`}
              >
                <PresentationChartBarIcon className="h-5 w-5" />
                <span>PPTs</span>
                <input
                  type="checkbox"
                  checked={visibilitySettings.ppt}
                  onChange={(e) => { e.stopPropagation(); setVisibilitySettings(prev => ({ ...prev, ppt: e.target.checked })); }}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  title="Show PPTs section on course page (uncheck to hide)"
                  onClick={(e) => e.stopPropagation()}
                />
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("question")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                  activeTab === "question"
                    ? "bg-emerald-600 text-white shadow-md"
                    : "bg-white text-gray-700 border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50"
                }`}
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
                <span>Quiz</span>
                <input
                  type="checkbox"
                  checked={visibilitySettings.question}
                  onChange={(e) => { e.stopPropagation(); setVisibilitySettings(prev => ({ ...prev, question: e.target.checked })); }}
                  className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  title="Show Quiz section on course page (uncheck to hide)"
                  onClick={(e) => e.stopPropagation()}
                />
              </button>
            </div>
          </div>
          {/* Right: course name (top) + class name (below); long class name wraps to next line */}
          <div className="flex-shrink-0 text-right max-w-[220px] sm:max-w-[280px]">
            <div className="text-sm font-semibold text-gray-700 truncate">
              {courseName && courseName.trim() ? courseName.trim() : "—"}
            </div>
            <div className="text-base font-medium text-gray-900 mt-0.5 break-words line-clamp-2 text-right">
              {lesson?.title ? lesson.title : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable content only (between header and footer) — full width, same for all sections */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
        <div className="w-full min-w-0">
        {/* Class Form */}
        {activeTab === "class" && (
          <form id="class-form" onSubmit={handleSaveClass} className="w-full space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Name {visibilitySettings.class && "*"}
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter class name"
                value={classFormData.title}
                onChange={handleClassInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                required={visibilitySettings.class}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Class Description
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <RibbonTiptapEditor
                  value={classFormData.description}
                  onChange={(val) =>
                    setClassFormData((prev) => ({ ...prev, description: val }))
                  }
                  placeholder="Write a detailed description of this class..."
                  minHeight="200px"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes) - Optional
                </label>
                <input
                  type="number"
                  name="duration"
                  placeholder="Enter duration in minutes (optional)"
                  value={classFormData.duration}
                  onChange={handleClassInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  min={0}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video File
                </label>
                <input
                  type="file"
                  name="file"
                  accept="video/*"
                  onChange={handleClassInputChange}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum file size: 5GB (server limit). Supported formats: MP4,
                  AVI, MOV, WMV, FLV, WebM
                </p>
                {classFormData.file &&
                  classFormData.file.size > 100 * 1024 * 1024 && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Large file detected. Upload may take several minutes.
                      Please be patient.
                    </p>
                  )}
                {classFormData.file && (
                  <p className="text-xs text-blue-600 mt-1">
                    File size:{" "}
                    {(classFormData.file.size / 1024 / 1024).toFixed(2)}MB
                    {classFormData.file.size > 5 * 1024 * 1024 * 1024 && (
                      <span className="text-red-600 ml-2">
                        ⚠️ File too large for server
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube Video Link
              </label>
              <input
                type="text"
                name="url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={classFormData.url}
                onChange={handleClassInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {activeTab === "class" &&
              (classFormData.file ||
                classFormData.url ||
                classFormData.videoUrl) && (
                <div className="my-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video Preview
                  </label>
                  {classFormData.file ? (
                    <video
                      key={`${
                        classFormData.videoUrl || lesson.videoUrl
                      }-${videoUpdateKey}`}
                      controls
                      className="w-full max-w-xl rounded shadow"
                    >
                      <source
                        src={
                          classFormData.file
                            ? URL.createObjectURL(classFormData.file)
                            : ""
                        }
                        type={classFormData.file?.type || "video/mp4"}
                      />
                      Your browser does not support the video tag.
                    </video>
                  ) : null}
                  {classFormData.videoUrl ? (
                    <video
                      key={`${
                        classFormData.videoUrl || lesson.videoUrl
                      }-${videoUpdateKey}`}
                      controls
                      className="w-full max-w-xl rounded shadow"
                    >
                      <source
                        src={`${API_URL}${classFormData.videoUrl}`}
                        type="video/mp4"
                      />
                      Your browser does not support the video tag.
                    </video>
                  ) : null}
                  {classFormData.url &&
                  classFormData.url.includes("youtube.com") ? (
                    <div className="aspect-w-16 aspect-h-9 w-full max-w-xl rounded shadow">
                      <iframe
                        className="w-full h-64"
                        src={`https://www.youtube.com/embed/${
                          classFormData.url.split("v=")[1]?.split("&")[0] || ""
                        }`}
                        title="YouTube Video Preview"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : null}
                </div>
              )}

            {/* Upload Progress Bar */}
            {uploading && uploadProgress > 0 && (
              <div className="mb-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-cyan-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Please don't close this window during upload
                </p>
                {classFormData.file && (
                  <p className="text-xs text-blue-600 mt-1">
                    File: {classFormData.file.name} (
                    {(classFormData.file.size / 1024 / 1024).toFixed(1)}MB)
                  </p>
                )}
              </div>
            )}

          </form>
        )}

        {/* Enhanced Notes Form */}
        {activeTab === "note" && (
          <form id="note-form" onSubmit={handleSaveNote} className="w-full space-y-6">
            {/* Note Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note Title {visibilitySettings.note && "*"}
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter note title"
                value={noteFormData.title}
                onChange={handleNoteInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                required={visibilitySettings.note}
              />
            </div>

            {/* Advanced Editor Section */}
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note Content
                </label>
              </div>

              <div className="border border-gray-300 rounded-lg overflow-hidden shadow-lg">
                {/* Professional Editor Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-3 sm:px-4 py-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                        <svg
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800">
                          Notes Editor
                        </h3>
                        <p className="text-xs text-gray-500">
                          Create and format your notes with advanced tools
                        </p>
                      </div>
                    </div>

                    {/* Professional Upload Toolbar */}
                    <div className="flex flex-wrap items-center gap-1">
                      {/* Divider */}
                      <div className="w-px h-6 bg-gray-300 mx-2 hidden sm:block"></div>

                      {/* Text File Upload */}
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.createElement("input");
                          input.setAttribute("type", "file");
                          input.setAttribute("accept", ".txt,.doc,.docx");
                          input.click();

                          input.onchange = () => {
                            const file = input.files[0];
                            if (file) {
                              setNoteFormData((prev) => ({
                                ...prev,
                                file: file,
                              }));

                              if (
                                file.type === "text/plain" ||
                                file.name.endsWith(".txt")
                              ) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  const content = event.target.result;
                                  setNoteFormData((prev) => ({
                                    ...prev,
                                    content: prev.content + "\n\n" + content,
                                  }));
                                  toast.success(
                                    "Text file content loaded into notes!"
                                  );
                                };
                                reader.readAsText(file);
                              } else {
                                toast.info(
                                  "Document file selected. Content will be available as download."
                                );
                              }
                            }
                          };
                        }}
                        className="group relative flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all duration-200 shadow-sm"
                        title="Upload Text File"
                      >
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span className="hidden sm:inline">Text File</span>
                        <span className="sm:hidden">TXT</span>
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </button>

                      {/* PDF Upload */}
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.createElement("input");
                          input.setAttribute("type", "file");
                          input.setAttribute("accept", "application/pdf");
                          input.click();

                          input.onchange = () => {
                            const file = input.files[0];
                            if (file) {
                              setNoteFormData((prev) => ({
                                ...prev,
                                file: file,
                              }));
                              toast.info(
                                "PDF file selected. Content will be available as download."
                              );
                            }
                          };
                        }}
                        className="group relative flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700 transition-all duration-200 shadow-sm"
                        title="Upload PDF"
                      >
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="hidden sm:inline">PDF</span>
                        <span className="sm:hidden">PDF</span>
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </button>

                      {/* Multiple Files Upload */}
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.createElement("input");
                          input.setAttribute("type", "file");
                          input.setAttribute("multiple", "true");
                          input.setAttribute(
                            "accept",
                            ".txt,.doc,.docx,image/*"
                          );
                          input.click();

                          input.onchange = () => {
                            const files = Array.from(input.files);
                            files.forEach((file) => {
                              if (
                                file.type === "text/plain" ||
                                file.name.endsWith(".txt")
                              ) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  const content = event.target.result;
                                  setNoteFormData((prev) => ({
                                    ...prev,
                                    content:
                                      prev.content +
                                      "\n\n--- Content from " +
                                      file.name +
                                      " ---\n" +
                                      content,
                                  }));
                                  toast.success(
                                    `Content from ${file.name} added to notes!`
                                  );
                                };
                                reader.readAsText(file);
                              }
                            });
                          };
                        }}
                        className="group relative flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 transition-all duration-200 shadow-sm"
                        title="Upload Multiple Files"
                      >
                        <svg
                          className="w-3 h-3 sm:w-4 sm:h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                        <span className="hidden sm:inline">Multiple</span>
                        <span className="sm:hidden">Multi</span>
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </button>
                    </div>
                  </div>
                </div>
                <RibbonTiptapEditor
                  value={noteFormData.content}
                  onChange={(val) =>
                    setNoteFormData((prev) => ({ ...prev, content: val }))
                  }
                  placeholder="Write your notes here with rich formatting, font sizes, and colors..."
                  minHeight="220px"
                />
              </div>

              {/* Content Size Indicator */}
              {noteFormData.content && (
                <div className="mt-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">
                      Content size:{" "}
                      {(noteFormData.content.length / 1024).toFixed(1)}KB
                    </span>
                    <span
                      className={`font-medium ${
                        noteFormData.content.length > 400000
                          ? "text-red-600"
                          : noteFormData.content.length > 200000
                          ? "text-yellow-600"
                          : "text-green-600"
                      }`}
                    >
                      {noteFormData.content.length > 400000
                        ? "⚠️ Very large content"
                        : noteFormData.content.length > 200000
                        ? "⚠️ Large content"
                        : "✅ Good size"}
                    </span>
                  </div>
                  {noteFormData.content.length > 400000 && (
                    <p className="text-xs text-red-600 mt-1">
                      Content is very large and may cause issues. Consider
                      removing some images or text.
                    </p>
                  )}
                </div>
              )}

              {/* Content Preview */}
              {noteFormData.content && (
                <div className="mt-4">
                  <details className="bg-gray-50 rounded-lg">
                    <summary className="px-4 py-2 cursor-pointer text-sm font-medium text-gray-700 hover:bg-gray-100">
                      📄 Preview Content
                    </summary>
                    <div className="px-4 pb-4">
                      <div
                        className="quill-preview max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: noteFormData.content,
                        }}
                      />
                    </div>
                  </details>
                </div>
              )}
            </div>

          </form>
        )}

        {/* Enhanced PPT Form */}
        {activeTab === "ppt" && (
          <>
            <form 
              key={`ppt-form-${ppt?.id || 'new'}`}
              id="ppt-form"
              onSubmit={handleSavePpt} 
              className="w-full space-y-4 mb-4"
            >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presentation Title {visibilitySettings.ppt && "*"}
              </label>
              <input
                type="text"
                name="title"
                placeholder="Enter presentation title"
                value={pptFormData.title || ""}
                onChange={handlePptInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                required={visibilitySettings.ppt}
                key={`ppt-title-${ppt?.id || 'new'}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presentation File {visibilitySettings.ppt && "*"}
              </label>
              <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 bg-purple-50 hover:bg-purple-100 transition-colors">
                <input
                  type="file"
                  name="file"
                  accept="application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={handlePptInputChange}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                  required={visibilitySettings.ppt && !ppt?.url && !ppt?.fileUrl}
                  disabled={!lesson || !lesson.id}
                />
                {(lesson === null || lesson.id === null) && (
                  <div className="text-xs text-red-500 mt-2">
                    Please save the lesson first before uploading a presentation
                    file.
                  </div>
                )}
                <div className="mt-3 text-xs text-gray-600">
                  <p className="font-medium mb-1">Supported formats:</p>
                  <ul className="space-y-1">
                    <li>• PowerPoint (.ppt, .pptx)</li>
                    <li>• PDF (.pdf) - Will be displayed inline</li>
                    <li>• Maximum file size: 50MB</li>
                  </ul>
                </div>
                {pptFormData.file && (
                  <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-500 rounded flex items-center justify-center">
                        <span className="text-white text-sm">📑</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {pptFormData.file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(pptFormData.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Show existing PPT file if available */}
            {(ppt?.fileUrl || ppt?.url) && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Current Presentation File:
                </p>
                {(ppt.fileUrl || ppt.url) && (
                  <div className="flex items-center gap-2">
                    {(ppt.fileUrl || ppt.url).endsWith(".pdf") ||
                    (ppt.fileUrl || ppt.url).includes(".pdf") ? (
                      <a
                        href={
                          ppt.fileUrl
                            ? `${API_URL}/lessons/content-file/${ppt.fileUrl.replace(
                                "/uploads/",
                                ""
                              )}`
                            : ppt.url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
                      >
                        📄 View PDF
                      </a>
                    ) : (ppt.fileUrl || ppt.url).endsWith(".ppt") ||
                      (ppt.fileUrl || ppt.url).endsWith(".pptx") ||
                      (ppt.fileUrl || ppt.url).includes(".ppt") ? (
                      <a
                        href={
                          ppt.fileUrl
                            ? `${API_URL}/lessons/content-file/${ppt.fileUrl.replace(
                                "/uploads/",
                                ""
                              )}`
                            : ppt.url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                        download
                      >
                        📑 Download PPT
                      </a>
                    ) : (
                      <a
                        href={ppt.fileUrl || ppt.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm font-medium"
                      >
                        📎 View File
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {/* Full-featured ReactQuill for ppt description */}
                <ReactQuill
                  key={`ppt-quill-${ppt?.id || 'new'}`}
                  value={pptFormData.content || ""}
                  onChange={(val) =>
                    setPptFormData((prev) => ({ ...prev, content: val }))
                  }
                  theme="snow"
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link", "image", "video"],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "header",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "list",
                    "bullet",
                    "link",
                    "image",
                    "video",
                  ]}
                  placeholder="Add a description, key points, or notes about this presentation..."
                  style={{ minHeight: "150px" }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Mode
                </label>
                <select
                  name="displayMode"
                  value={pptFormData.displayMode || "inline"}
                  onChange={handlePptInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="inline">Inline Viewer (Recommended)</option>
                  <option value="download">Download Only</option>
                  <option value="both">Both Options</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Inline viewer shows the presentation directly on the page
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto-advance Slides (seconds)
                </label>
                <input
                  type="number"
                  name="autoAdvance"
                  placeholder="0 = manual advance"
                  value={pptFormData.autoAdvance || 0}
                  onChange={handlePptInputChange}
                  min="0"
                  max="60"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Set to 0 for manual navigation
                </p>
              </div>
            </div>

            </form>
            
            {/* List of All Existing PPTs */}
            {ppts.length > 0 && (
            <div className="mt-6">
              <h4 className="text-md font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <PresentationChartBarIcon className="h-5 w-5 text-purple-500" />
                All Presentations ({ppts.length})
              </h4>
              <ul className="space-y-3">
                {ppts.map((pptItem, idx) => (
                  <li
                    key={pptItem.id}
                    className="bg-purple-50 border border-purple-200 rounded-lg px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-800 text-sm">
                          {idx + 1}.
                        </span>
                        <div className="flex-1">
                          <span className="text-gray-700 text-sm break-words font-medium">
                            {pptItem.title || "Untitled Presentation"}
                          </span>
                          {pptItem.description && (
                            <p className="text-xs text-gray-500 mt-1 break-words">
                              {stripHtmlTags(pptItem.description)}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                              Display: {pptItem.displayMode || pptItem.display_mode || "inline"}
                            </span>
                            {(pptItem.autoAdvance || pptItem.auto_advance) > 0 && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                Auto: {pptItem.autoAdvance || pptItem.auto_advance}s
                              </span>
                            )}
                            {(pptItem.fileUrl || pptItem.url) && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                📎 File attached
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                      <button
                        type="button"
                        title="Edit"
                        className="p-1 rounded hover:bg-purple-100 text-purple-700"
                        onClick={() => {
                          lastSavedPptUrlRef.current = null;
                          // Load this PPT into the form for editing
                          setPptFormData({
                            title: pptItem.title || "",
                            description: pptItem.description || "",
                            type: "ppt",
                            url: pptItem.url || "",
                            content: pptItem.content || "",
                            duration: pptItem.duration || 0,
                            file: null,
                            displayMode: pptItem.displayMode || pptItem.display_mode || "inline",
                            autoAdvance: pptItem.autoAdvance || pptItem.auto_advance || 0,
                          });
                          // Scroll to form
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        <PencilIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <button
                        type="button"
                        title="Delete"
                        className="p-1 rounded hover:bg-red-100 text-red-700"
                        onClick={() => handleDeleteContent(lesson.id, pptItem.id)}
                      >
                        <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            )}

            {/* PPT/PDF Preview section at bottom */}
            <div className="mt-6 w-full border-2 border-purple-200 rounded-xl bg-purple-50/50 overflow-hidden">
              <h4 className="text-sm font-semibold text-purple-800 px-4 py-3 border-b border-purple-200 bg-white/80">
                PPT / PDF Preview
              </h4>
              <div className="p-4 min-h-[200px]">
                {(() => {
                  // Server URL (saved) or blob URL (selected file before save)
                  const previewUrl =
                    pptFormData.url ||
                    (ppt && (ppt.url || ppt.fileUrl)) ||
                    lastSavedPptUrlRef.current ||
                    (pptFormData.file && isPdfFile(pptFormData.file) ? pptPreviewBlobUrl : null);
                  const hasSelectedFile = pptFormData.file != null;
                  if (!previewUrl) {
                    if (hasSelectedFile && !isPdfFile(pptFormData.file)) {
                      return (
                        <div className="space-y-2">
                          <p className="text-gray-700 text-sm">
                            File selected: <strong>{pptFormData.file.name}</strong>
                          </p>
                          <p className="text-gray-500 text-sm">
                            This format will be available for <strong>download</strong> on the course page. Display mode &quot;{pptFormData.displayMode || "inline"}&quot; applies to PDF only for inline view.
                          </p>
                          <p className="text-gray-500 text-xs">Save to publish.</p>
                        </div>
                      );
                    }
                    if (hasSelectedFile && isPdfFile(pptFormData.file)) {
                      if (pptFormData.file.size > MAX_PDF_PREVIEW_SIZE) {
                        return (
                          <p className="text-gray-500 text-sm">
                            PDF is large ({(pptFormData.file.size / 1024 / 1024).toFixed(1)} MB). Save and open from the course page to view.
                          </p>
                        );
                      }
                      if (!pptPreviewBlobUrl) return <p className="text-gray-500 text-sm">Loading PDF preview…</p>;
                    }
                    return (
                      <p className="text-gray-500 text-sm">
                        Save a presentation with a file to see preview here. Display mode &quot;{pptFormData.displayMode || "inline"}&quot; will apply on the course content page.
                      </p>
                    );
                  }
                  const isPdf =
                    String(previewUrl).toLowerCase().endsWith(".pdf") ||
                    String(previewUrl).includes(".pdf") ||
                    previewUrl.startsWith("blob:") ||
                    (previewUrl.startsWith("data:") && pptFormData.file && isPdfFile(pptFormData.file));
                  let fileUrl =
                    String(previewUrl).startsWith("http") || String(previewUrl).startsWith("blob:") || String(previewUrl).startsWith("data:")
                      ? previewUrl
                      : `${API_URL}/files/${String(previewUrl).replace(/^\/+/, "")}`;
                  if (String(fileUrl).startsWith("http") && !fileUrl.startsWith("blob:") && !fileUrl.startsWith("data:")) {
                    try {
                      const u = new URL(fileUrl);
                      if (u.hostname === "isyllabi.com" || u.hostname === "www.isyllabi.com") {
                        fileUrl = `${API_URL}/files/embed?url=${encodeURIComponent(fileUrl)}`;
                      }
                    } catch (_) {}
                  }
                  const isBlobOrDataUrl = fileUrl.startsWith("blob:") || fileUrl.startsWith("data:");
                  return (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-600">
                        Display mode: <strong>{pptFormData.displayMode || "inline"}</strong> — this is how it will show on the iSyllabi content page.
                      </p>
                      {isPdf ? (
                        <div className="space-y-2">
                          <div className="w-full rounded-lg border border-purple-200 bg-white overflow-hidden" style={{ height: "360px" }}>
                            <embed
                              key={fileUrl}
                              src={fileUrl}
                              type="application/pdf"
                              className="w-full h-full border-0"
                              style={{ minHeight: "360px", display: "block" }}
                              title="PDF preview"
                            />
                          </div>
                          <p className="text-xs text-gray-500">
                            {isBlobOrDataUrl ? (
                              <>Selected file (not saved yet).{" "}
                                <a href={fileUrl} download={pptFormData.file?.name || "presentation.pdf"} className="text-purple-600 hover:underline">
                                  Download PDF
                                </a>{" "}
                                or save to view on the course page.
                              </>
                            ) : (
                              <>
                                If the PDF does not show above,{" "}
                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                                  open it in a new tab
                                </a>.
                              </>
                            )}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-purple-200">
                          <span className="text-purple-600">📑</span>
                          <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-purple-700 font-medium hover:underline">
                            Open / Download presentation
                          </a>
                          <span className="text-gray-500 text-sm">(Preview not available for this format in admin)</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </>
        )}

        {/* Quiz section: Add Quiz opens popup; list shows existing with Edit/Delete */}
        {activeTab === "question" && (
          <>
            <div className="flex justify-end mb-2">
              <button
                type="button"
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded shadow text-sm disabled:opacity-50"
                disabled={!lesson?.id}
                onClick={() => {
                  setQuizPopEditContent(null);
                  setShowQuizPop(true);
                }}
              >
                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Add Quiz</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
            <div className="mt-2">
              <h4 className="text-md font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <QuestionMarkCircleIcon className="h-5 w-5 text-green-500" />{" "}
                Quiz
              </h4>
              {contents.filter((c) => c.type === "question").length === 0 && (
                <div className="text-gray-500 italic">
                  No questions added yet. Click &quot;Add Quiz&quot; to create one.
                </div>
              )}
              <ul className="space-y-3">
                {contents
                  .filter((c) => c.type === "question")
                  .map((q, idx) => {
                    let parsed = null;
                    try {
                      parsed = JSON.parse(q.content);
                    } catch {}
                    return (
                      <li
                        key={q.id}
                        className="bg-green-50 border border-green-200 rounded-lg px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-gray-800 text-sm">
                              {idx + 1}.
                            </span>
                            <span className="text-gray-700 text-sm break-words">
                              {parsed?.question || q.title}
                            </span>
                          </div>
                          {(parsed?.description || q.description) && (
                            <span className="block text-xs text-gray-500 mt-1 break-words">
                              {parsed?.description || q.description}
                            </span>
                          )}
                          {parsed && Array.isArray(parsed.options) && (
                            <div className="mt-2 text-xs text-gray-500">
                              <span className="font-medium">Options:</span>
                              <div className="mt-1 space-y-1">
                                {parsed.options.map((opt, i) => (
                                  <div
                                    key={i}
                                    className={`${
                                      parsed.correctAnswer === i
                                        ? "text-green-700 font-bold"
                                        : ""
                                    }`}
                                  >
                                    {String.fromCharCode(65 + i)}.{" "}
                                    {typeof opt === "string" ? opt : opt.text}
                                    {parsed.correctAnswer === i &&
                                      parsed.correctOptionDesc && (
                                        <div className="text-green-700 font-normal ml-4 mt-1">
                                          Explanation:{" "}
                                          {parsed.correctOptionDesc}
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                          <button
                            type="button"
                            title="Edit"
                            className="p-1.5 rounded hover:bg-green-100 text-green-700"
                            onClick={() => {
                              setQuizPopEditContent(q);
                              setShowQuizPop(true);
                            }}
                          >
                            <PencilIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            className="p-1.5 rounded hover:bg-red-100 text-red-700"
                            onClick={() => handleDeleteContent(lesson.id, q.id)}
                          >
                            <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
              </ul>
            </div>
            <QuizPop
              isOpen={showQuizPop}
              onClose={() => {
                setShowQuizPop(false);
                setQuizPopEditContent(null);
              }}
              lessonId={lesson?.id}
              editContent={quizPopEditContent}
              API_URL={API_URL}
              onSuccess={async () => {
                await refetchLessonContents();
                try {
                  const token = localStorage.getItem("token");
                  await axios.patch(
                    `${API_URL}/lessons/${lesson.id}/visibility`,
                    {
                      show_class: visibilitySettings.class ? 1 : 0,
                      show_note: visibilitySettings.note ? 1 : 0,
                      show_ppt: visibilitySettings.ppt ? 1 : 0,
                      show_question: 1,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                } catch (err) {
                  /* non-blocking */
                }
                setVisibilitySettings((prev) => ({ ...prev, question: true }));
                if (onContentUpdate) onContentUpdate();
              }}
            />
          </>
        )}

        {/* Video Preview for Class Tab - inside scrollable area so footer stays fixed */}
        {activeTab === "class" && (
          <>
            {(localVideoUrl || classFormData.videoUrl || lesson.videoUrl) && (
              <div className="my-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {localVideoUrl ? "New Video Preview:" : "Uploaded Video Preview:"}
                </label>
                <video
                  key={`${localVideoUrl || classFormData.videoUrl || lesson.videoUrl}-${videoUpdateKey}`}
                  controls
                  className="w-full max-w-xl rounded shadow"
                >
                  <source
                    src={localVideoUrl || `${API_URL}${classFormData.videoUrl || lesson.videoUrl}`}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
                {localVideoUrl && (
                  <p className="text-xs text-blue-600 mt-1">
                    ⚠️ This is a preview of your new video. Save the class to upload it to the server.
                  </p>
                )}
              </div>
            )}
            {(classFormData.url || lesson.url) &&
              ((classFormData.url || lesson.url).includes("youtube.com") || (classFormData.url || lesson.url).includes("youtu.be")) && (
              <div className="my-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Video Preview:</label>
                <div className="aspect-w-16 aspect-h-9 w-full rounded overflow-hidden border">
                  <iframe
                    src={`https://www.youtube.com/embed/${((classFormData.url || lesson.url).split("v=")[1] || (classFormData.url || lesson.url).split("youtu.be/")[1] || "").split("&")[0]}`}
                    title="YouTube video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-48 sm:h-64 md:h-80"
                  />
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* Footer: Cancel + Save (non-scrollable) */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50/80 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={async () => {
                const ok = await confirm({
                  title: "Cancel changes?",
                  message: "Are you sure you want to cancel? Unsaved changes will be lost.",
                  confirmLabel: "Yes, discard",
                  cancelLabel: "Keep editing",
                  variant: "warning",
                });
                if (!ok) return;
                switch(activeTab) {
                  case 'class':
                    setClassFormData({
                      title: lesson?.title || "",
                      description: lesson?.description || "",
                      type: "video",
                      url: lesson?.url || "",
                      content: "",
                      duration: lesson?.duration || 0,
                      file: null,
                      videoUrl: lesson?.videoUrl || "",
                    });
                    break;
                  case 'note':
                    setNoteFormData({ title: "", type: "note", url: "", content: "", duration: 0, file: null });
                    break;
                  case 'ppt':
                    setPptFormData({ title: "", description: "", type: "ppt", url: "", content: "", duration: 0, file: null, displayMode: "inline", autoAdvance: 0 });
                    break;
                  case 'question':
                    setShowQuizPop(false);
                    setQuizPopEditContent(null);
                    setShowQuestionForm(false);
                    setEditingQuestionId(null);
                    setQuestionFormData({
                      title: "", description: "", type: "question", url: "", content: "", duration: 0, file: null,
                      options: [{ text: "", desc: "" }, { text: "", desc: "" }, { text: "", desc: "" }, { text: "", desc: "" }],
                      correctAnswer: 0, _new: Date.now(),
                    });
                    break;
                }
                toast.info("Changes cancelled");
              }}
              className="px-5 py-2.5 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={uploading || instantFeedback || (!lesson || !lesson.id)}
              className={`px-5 py-2.5 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${
                uploading || instantFeedback || (!lesson || !lesson.id)
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-indigo-600 hover:bg-indigo-700"
              }`}
              title={(!lesson || !lesson.id) ? "Save the lesson first" : "Save all sections (Class, Notes, PPTs, Quiz)"}
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading {uploadProgress}%
                </>
              ) : instantFeedback ? (
                <>Saving all...</>
              ) : (
                <>Save all</>
              )}
            </button>
        </div>
      </div>

      {/* Instant Feedback Indicator */}
      {instantFeedback && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg animate-pulse">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
            <span className="text-sm font-medium">Action in progress...</span>
          </div>
        </div>
      )}

      {/* Optimistic Updates Indicator */}
      {Object.keys(optimisticUpdates).length > 0 && (
        <div className="fixed top-16 right-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-spin"></div>
            <span className="text-sm font-medium">
              {Object.keys(optimisticUpdates).length} update(s) pending...
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseContentManagerModal;
